import { useCallback, useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Music2, Square } from "lucide-react";
import { useDance } from "@/lib/useDance";
import { routineOrder, stepFor } from "@/lib/choreo";
import { play, soundOn, setSoundOn, unlockAudio } from "@/lib/sfx";
import { motion, useAnimationControls, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Sparkle } from "@/components/icons";
import { useSite } from "@/context/SiteContext";
import { useI18n } from "@/i18n/I18nContext";

const POSES = ["front", "sideL", "sideR", "winkwave", "jump", "star", "heart", "sleep", "wave3q", "back"];
const CLICK_POSES = ["jump", "star", "heart", "winkwave"];
const POSE_SOUND = { jump: "boing", star: "sparkle", heart: "kiss", winkwave: "giggle" };
const TOUCH_TOUR = ["front", "sideL", "front", "sideR", "wave3q"];
const BURST_COLORS = ["#ff4d8d", "#ffd23f", "#38b6ff", "#2fb344", "#7c4dff", "#ff7a2f"];
const clamp = (v) => Math.max(-1, Math.min(1, v));

const PoseLayer = ({ pose }) => {
  const { t } = useI18n();
  return (
  <>
    {POSES.map((p) => (
      <motion.img
        key={p}
        src={`/brand/${p}.webp`}
        alt={t.imgAlt.mascot}
        draggable={false}
        className="absolute inset-0 w-full h-full object-contain object-bottom select-none pointer-events-none"
        initial={false}
        animate={{ opacity: pose === p ? 1 : 0, scale: pose === p ? 1 : 0.92 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      />
    ))}
  </>
  );
};

const Burst = ({ bursts }) => (
  <div className="absolute inset-0 pointer-events-none">
    {bursts.map((b) => (
      <motion.span
        key={b.id}
        className="absolute left-1/2 top-1/2"
        initial={{ x: 0, y: 0, scale: 0.3, opacity: 1, rotate: 0 }}
        animate={{ x: b.x, y: b.y, scale: 1, opacity: 0, rotate: b.r }}
        transition={{ duration: 0.9, ease: "easeOut" }}
      >
        <Sparkle className="w-6 h-6 -ml-3 -mt-3" color={b.c} />
      </motion.span>
    ))}
  </div>
);

export const Mascot = ({ bubbles = [], className = "", danceTracks = [], danceHint = "" }) => {
  const { setParty } = useSite();
  const [song, setSong] = useState(null);
  const lastSong = useRef(null);
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const spring = { stiffness: 150, damping: 15 };
  const rotY = useSpring(useTransform(mx, [-1, 1], [-22, 22]), spring);
  const rotX = useSpring(useTransform(my, [-1, 1], [14, -14]), spring);
  const tx = useSpring(useTransform(mx, [-1, 1], [-26, 26]), spring);
  const [pose, setPose] = useState("front");
  const [bubble, setBubble] = useState(null);
  const [bursts, setBursts] = useState([]);
  const squash = useAnimationControls();
  const lock = useRef(0);
  const lastMove = useRef(Date.now());
  const onScreen = useRef(true);
  const hovering = useRef(false);
  const clicks = useRef(0);
  const timers = useRef([]);
  const poseRef = useRef("front");
  const [sound, setSound] = useState(soundOn);
  const go = useCallback((p) => {
    poseRef.current = p;
    setPose(p);
  }, []);
  const tapTimes = useRef([]);
  const order = useRef(routineOrder(""));
  const danceCtl = useAnimationControls();
  const onBeat = (i, period) => {
    const step = stepFor(order.current, i);
    go(step.pose);
    mx.set(step.pose === "sideL" ? -0.5 : step.pose === "sideR" ? 0.5 : 0);
    danceCtl.start({ ...step.move, transition: { duration: Math.max(0.25, period * 0.92), ease: "easeOut" } });
    window.dispatchEvent(new Event("ananawi-beat"));
  };
  const { dancing, bpm, start: startDance, stop: stopDance } = useDance(onBeat);
  useEffect(() => {
    setParty(dancing);
  }, [dancing, setParty]);
  useEffect(() => {
    if (!dancing) {
      danceCtl.start({ y: 0, rotate: 0, x: 0, scale: 1, transition: { duration: 0.3 } });
      lock.current = 0;
      lastMove.current = Date.now();
      go("front");
      mx.set(0);
    }
  }, [dancing, go, mx, danceCtl]);

  useEffect(() => {
    const onMove = (e) => {
      if (e.pointerType === "touch" || !onScreen.current) return;
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const dx = clamp((e.clientX - (r.left + r.width / 2)) / (window.innerWidth / 2));
      const dy = clamp((e.clientY - (r.top + r.height / 2)) / (window.innerHeight / 2));
      mx.set(dx);
      my.set(dy);
      lastMove.current = Date.now();
      if (Date.now() < lock.current || hovering.current) return;
      if (poseRef.current === "sleep") {
        lock.current = Date.now() + 900;
        play("wake");
        go("jump");
        return;
      }
      go(dx < -0.28 ? "sideL" : dx > 0.28 ? "sideR" : "front");
    };
    window.addEventListener("pointermove", onMove);
    const io = new IntersectionObserver(([e]) => { onScreen.current = e.isIntersecting; });
    if (ref.current) io.observe(ref.current);
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    let step = 0;
    const tick = setInterval(() => {
      // Paused while the mascot is off screen or the tab is hidden (no pose swaps / sounds in background).
      if (document.visibilityState !== "visible" || !onScreen.current) return;
      if (Date.now() < lock.current) return;
      if (coarse) {
        step = (step + 1) % TOUCH_TOUR.length;
        go(TOUCH_TOUR[step]);
        mx.set(TOUCH_TOUR[step] === "sideL" ? -0.5 : TOUCH_TOUR[step] === "sideR" ? 0.5 : 0);
      } else if (Date.now() - lastMove.current > 8000 && poseRef.current !== "sleep") {
        play("snore");
        go("sleep");
      }
    }, coarse ? 2600 : 1000);
    const pending = timers.current;
    return () => {
      window.removeEventListener("pointermove", onMove);
      io.disconnect();
      clearInterval(tick);
      pending.forEach(clearTimeout);
    };
  }, [mx, my, go]);

  const later = (fn, ms) => {
    timers.current.push(setTimeout(fn, ms));
  };

  const onTap = () => {
    unlockAudio();
    if (dancing) {
      stopDance();
      return;
    }
    const now = Date.now();
    tapTimes.current = [...tapTimes.current.filter((x) => now - x < 1200), now];
    if (tapTimes.current.length >= 3) {
      tapTimes.current = [];
      lock.current = now + 10 * 60 * 1000;
      const pool = danceTracks.length > 1 ? danceTracks.filter((x) => x.id !== lastSong.current) : danceTracks;
      const pick = pool[Math.floor(Math.random() * pool.length)] ?? null;
      lastSong.current = pick?.id ?? null;
      setSong(pick);
      order.current = routineOrder(pick?.id);
      startDance(pick?.preview_url, pick?.tempo);
      return;
    }
    clicks.current += 1;
    const next = CLICK_POSES[clicks.current % CLICK_POSES.length];
    lock.current = Date.now() + 1500;
    lastMove.current = Date.now();
    later(() => play(POSE_SOUND[next]), 20);
    go(next);
    squash.start({ scaleY: [1, 0.82, 1.12, 1], scaleX: [1, 1.12, 0.94, 1], transition: { duration: 0.6 } });
    if (bubbles.length) {
      const txt = bubbles[clicks.current % bubbles.length];
      setBubble({ txt, id: Date.now() });
      later(() => setBubble(null), 1900);
    }
    const id = Date.now();
    const pieces = Array.from({ length: 12 }, (_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const d = 140 + Math.random() * 80;
      return { id: `${id}-${i}`, x: Math.cos(a) * d, y: Math.sin(a) * d, r: Math.random() * 180, c: BURST_COLORS[i % 6] };
    });
    setBursts(pieces);
    later(() => setBursts([]), 1000);
  };

  return (
    <div ref={ref} className={`relative [perspective:1000px] ${className}`} data-testid="hero-mascot">
      {bubble && (
        <motion.div
          key={bubble.id}
          initial={{ opacity: 0, y: 10, scale: 0.6 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="absolute -top-2 right-0 sm:right-4 z-20 rounded-3xl sticker-sm bg-white px-4 py-2 font-display font-semibold text-ink text-base sm:text-lg"
          data-testid="mascot-speech-bubble"
        >
          {bubble.txt}
          <span className="absolute -bottom-[9px] left-8 w-4 h-4 bg-white border-r-[2.5px] border-b-[2.5px] border-ink rotate-45" />
        </motion.div>
      )}
      <Burst bursts={bursts} />
      {dancing && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute -top-4 left-0 z-20 flex items-center gap-2 rounded-full sticker-sm bg-pink text-white pl-3 pr-1 py-1 max-w-[90%]"
          data-testid="mascot-dance-badge"
        >
          <Music2 className="w-4 h-4 shrink-0 animate-bounce" />
          <span className="text-xs font-extrabold truncate">{song?.title || "Ananawi"}</span>
          {bpm && <span className="text-[12px] font-extrabold bg-white/25 rounded-full px-2 shrink-0" data-testid="mascot-dance-bpm">{bpm} BPM</span>}
          <button type="button" onClick={stopDance} className="grid place-items-center w-7 h-7 rounded-full bg-white text-ink shrink-0" aria-label="Stop" data-testid="mascot-dance-stop">
            <Square className="w-3 h-3 fill-current" />
          </button>
        </motion.div>
      )}
      {!dancing && danceHint && (
        <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-white/90 border-2 border-ink px-3 py-1 text-xs font-extrabold" data-testid="mascot-dance-hint">
          {danceHint}
        </span>
      )}
      <button
        type="button"
        onClick={() => {
          const next = !sound;
          setSoundOn(next);
          setSound(next);
          if (next) {
            unlockAudio();
            later(() => play("pop"), 30);
          }
        }}
        aria-label={sound ? "Audio on" : "Audio off"}
        className="absolute bottom-2 left-0 z-20 grid place-items-center w-11 h-11 rounded-full bg-white sticker-sm btn-pop btn-pop-sm"
        data-testid="mascot-sound-toggle"
      >
        {sound ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
      </button>
      <motion.button
        type="button"
        aria-label="Ananawi"
        data-testid="mascot-button"
        onClick={onTap}
        onPointerEnter={(e) => {
          if (e.pointerType === "touch") return;
          hovering.current = true;
          play("pop");
          if (Date.now() > lock.current) go("winkwave");
        }}
        onPointerLeave={() => {
          hovering.current = false;
        }}
        style={{ rotateY: rotY, rotateX: rotX, x: tx, transformStyle: "preserve-3d" }}
        className="relative block w-full aspect-[1/1.08] cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-pink rounded-full"
      >
        <motion.div
          className="absolute inset-0"
          animate={dancing ? { y: 0, rotate: 0 } : { y: [0, -14, 0], rotate: 0 }}
          transition={dancing ? { duration: 0.2 } : { duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        >
          <motion.div className="absolute inset-0 origin-bottom" animate={danceCtl}>
          <motion.div className="absolute inset-0 origin-bottom" animate={squash}>
            <PoseLayer pose={pose} />
          </motion.div>
          </motion.div>
        </motion.div>
        <motion.span
          className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-1/2 h-5 rounded-[50%] bg-ink/20 blur-[3px]"
          animate={{ scaleX: [1, 0.8, 1], opacity: [0.5, 0.3, 0.5] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.button>
      <span className="sr-only" data-testid="mascot-current-pose">{pose}</span>
    </div>
  );
};
